import { IdentityBodyAssess, IdentityDailyWeightLoss, IdentityMeasurementUnit } from '@reg/shared/model';
import result from 'lodash-es/result';
import { DateFormat } from '@reg/shared/enum';
import { format } from 'date-fns';
import { convertToDateTime } from '@reg/shared/helper/dateTime-helper';

/**
 * Model to hold a body assessment weigh in record
 */
export class IdentityBodyAssessWeighIn {
    public id: string = null;
    public weight: number = null;
    public eventId: string = null;
    public weighInDate: Date = null;
    public bodyAssessment: IdentityBodyAssess = new IdentityBodyAssess();
    public weightLossPlanDate: IdentityDailyWeightLoss = null;
    public isPlanExempt: boolean = false;
    public measurementUnit: IdentityMeasurementUnit;
    public teamId: string;

    public static getFields(): string[] {
        return [
            'weight',
            'eventId',
            'weighInDate',
            'bodyAssessment',
            'isPlanExempt',
            'measurementUnitId',
            'teamId',
        ];
    }

    /**
     * Sort wrestlers by this order: targetWeight, name (last, first), weigh in date
     *
     * @param {IdentityBodyAssessWeighIn[]} weighIns
     * @returns {IdentityBodyAssessWeighIn[]}
     */
    public static sort(weighIns: IdentityBodyAssessWeighIn[]): IdentityBodyAssessWeighIn[] {
        return weighIns.sort((w1: IdentityBodyAssessWeighIn, w2: IdentityBodyAssessWeighIn) => {
            const weight1: number = w1.weightLossPlanDate ? w1.weightLossPlanDate.targetWeight : 0;
            const weight2: number = w2.weightLossPlanDate ? w2.weightLossPlanDate.targetWeight : 0;

            if (weight1 > weight2) {
                return 1;
            } if (weight1 < weight2) {
                return -1;
            }
            // targetWeight's are equal so now sort by lastName, firstName
            const person1: string = <string>result(w1, 'bodyAssessment.person.formatNameLastFirst');
            const person2: string = <string>result(w2, 'bodyAssessment.person.formatNameLastFirst');

            if (person1 > person2) {
                return 1;
            } if (person1 < person2) {
                return -1;
            }
            // targetWeights and person are equal so now sort by weighInDate
            const date1: Date = w1.weighInDate;
            const date2: Date = w2.weighInDate;

            if (date1 > date2) {
                return 1;
            } if (date1 < date2) {
                return -1;
            }

            return 0;
        });
    }

    constructor() {
        this.measurementUnit = IdentityMeasurementUnit.createLbs();
    }

    public clone(): IdentityBodyAssessWeighIn {
        const clone: IdentityBodyAssessWeighIn = new IdentityBodyAssessWeighIn();
        clone.id = this.id;
        clone.weight = this.weight;
        clone.eventId = this.eventId;
        clone.teamId = this.teamId;
        clone.isPlanExempt = this.isPlanExempt;
        clone.bodyAssessment = this.bodyAssessment && this.bodyAssessment.clone();

        if (this.measurementUnit) {
            clone.measurementUnit = this.measurementUnit.clone();
        }

        if (this.weighInDate) {
            clone.weighInDate = new Date(this.weighInDate.getTime());
        }

        if (this.weightLossPlanDate) {
            clone.weightLossPlanDate = this.weightLossPlanDate.clone();
        }

        return clone;
    }

    /**
     * Serialize to a json api format
     * @returns {string}
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'bodyAssessmentWeighIn';
        json.data.attributes = {};
        json.data.relationships = {};

        json.data.id = this.id;
        json.data.attributes.weight = this.weight ? parseFloat(this.weight.toString()) : null;
        json.data.attributes.isPlanExempt = this.isPlanExempt || false;
        json.data.attributes.eventId = this.eventId;

        if (this.weighInDate) {
            json.data.attributes.weighInDate = format(this.weighInDate, DateFormat.YYYYMMDD);
        }

        if (this.measurementUnit) {
            json.data.relationships.measurementUnit = {
                data: { type: 'measurementUnit', id: this.measurementUnit.id },
            };
        }
        if (this.teamId) {
            json.data.relationships.team = { data: { type: 'team', id: this.teamId } };
        }

        return JSON.stringify(json);
    }

    /**
     * Deserialize from json api format
     */
    public deserialize(json: Object, included: any[] = null): IdentityBodyAssessWeighIn {
        const data: Object = json['data'] || json;
        const attrs: Object = data['attributes'];
        const relationships: Object = data['relationships'] || [];

        this.id = data['id'];
        this.weighInDate = attrs['weighInDate'] && convertToDateTime(attrs['weighInDate']);
        this.isPlanExempt = attrs['isPlanExempt'];
        this.eventId = attrs['eventId'];
        this.teamId = attrs['teamId'];
        this.weight = attrs['weight'];

        if (attrs['weightLossPlanDate']) {
            this.weightLossPlanDate = new IdentityDailyWeightLoss().deserialize(attrs['weightLossPlanDate']);
        }

        const measurementUnitLink: Object = relationships['measurementUnit'];
        if (measurementUnitLink &&
            included &&
            measurementUnitLink['data'] &&
            measurementUnitLink['data']['id']) {
            const measurementUnitId: string = measurementUnitLink['data']['id'];
            const measurementUnits: Object[] =
                included.filter((i: Object) => i['type'] === 'measurementUnit' && i['id'] === measurementUnitId);

            if (measurementUnits.length === 1) {
                this.measurementUnit = (new IdentityMeasurementUnit()).deserialize(measurementUnits[0]);
            }
        } else if (attrs['measurementUnitId']) {
            this.measurementUnit = new IdentityMeasurementUnit(attrs['measurementUnitId']);
        }

        const bodyAssessLink: Object = relationships['bodyAssessment'];
        if (bodyAssessLink &&
            included &&
            bodyAssessLink['data'] &&
            bodyAssessLink['data']['id']) {
            const bodyAssessId: string = bodyAssessLink['data']['id'];
            const bodyAssess: Object[] = included.filter((i: Object) => i['id'] === bodyAssessId);

            if (bodyAssess.length === 1) {
                this.bodyAssessment = (new IdentityBodyAssess()).deserialize(bodyAssess[0], included);
            }
        } else if (attrs['bodyAssessmentId']) {
            this.bodyAssessment = new IdentityBodyAssess();
            this.bodyAssessment.id = attrs['bodyAssessmentId'];
        }

        return this;
    }
}
